import { calculateHardeningFactor } from "./calculateHardeningFactor";
import { SCOOP_SIZE_GRAMS } from "./constants";
import {
  CalculatorIngredient,
  FirmnessResult,
  RecipeCalculations,
} from "./types";
import {
  getEvaporationModifier,
  getInfusionModifier,
} from "./ingredientModifiers";
import { clamp, toNumber } from "./utils";

/**
 * Mulitply this constant by the normalized hardening factor to get the
 * amount of frozen water that we need to adjust downward by.
 */
const HARDENING_FACTOR_FROZEN_WATER_COEFFICIENT = 1.0617;

/**
 * Anecdotally, I've found that Corvitto's numbers overestimate the amount of
 * hardening. This is meant to adjust the hardning factor to be a little more
 * conservative to match my personal experience.
 */
const PERSONAL_EXPERIENCE_HARDENING_FACTOR_COEFFICIENT = 0.3;

export type CalculationConfiguration = {
  evaporationPercentage: number;
  desiredFrozenWaterPercentage: number;
  servingTemperatureC?: number;
};

/**
 * For calculating freezing curves, this is a polynomial equation derived
 * from the freezing point table [1].
 *
 * [1]: Ice Cream 7th Edition, pg. 186
 */
export function calculateFreezingPointDepressionSimple({
  pac,
  waterPercentage,
  percentageWaterFrozen,
  msnfPercentage,
}: {
  pac: number;
  waterPercentage: number;
  percentageWaterFrozen: number;
  msnfPercentage: number;
}) {
  const waterNotFrozenProportion =
    waterPercentage * (1 - percentageWaterFrozen);

  // Normalize PAC to the amount of water in the mixture
  const normalizedPac = pac / waterNotFrozenProportion;

  // Equivalent g sucrose / 100g water
  const sucroseEquivalentWaterConcentration = 100 * normalizedPac;

  // Technically we are double-counting the salts from MSNF here because the PAC accounts
  // for all salt content, but I think the effect is negligible.
  //
  // 2.37 comes from the original paper, but it's a bit hand-wavy IMO. I do include it
  // because otherwise the freezing curve shows recipes as harder than they should be,
  // so maybe there's something to it.
  const msnfSaltContribution =
    -(msnfPercentage / waterNotFrozenProportion) * 2.37;

  return (
    -9 * 0.00001 * Math.pow(sucroseEquivalentWaterConcentration, 2) -
    0.0612 * sucroseEquivalentWaterConcentration +
    msnfSaltContribution
  );
}

/**
 * Inverts calculateFreezingPointDepressionSimple to find the fraction of the
 * recipe's water that is frozen at a fixed temperature.
 */
export function calculateFrozenWaterPercentageAtTemperature({
  pac,
  waterPercentage,
  msnfPercentage,
  temperatureC,
}: {
  pac: number;
  waterPercentage: number;
  msnfPercentage: number;
  temperatureC: number;
}): number | null {
  if (
    ![pac, waterPercentage, msnfPercentage, temperatureC].every(
      Number.isFinite,
    ) ||
    pac < 0 ||
    waterPercentage <= 0 ||
    waterPercentage > 1 ||
    msnfPercentage < 0 ||
    msnfPercentage > 1
  ) {
    return null;
  }

  const freezingPoint = calculateFreezingPointDepressionSimple({
    pac,
    waterPercentage,
    percentageWaterFrozen: 0,
    msnfPercentage,
  });

  if (!Number.isFinite(freezingPoint)) {
    return null;
  }

  if (temperatureC >= freezingPoint) {
    return 0;
  }

  // Expanding the forward equation with u = unfrozen water proportion gives:
  // -temperature = (0.9 * pac^2) / u^2 +
  //                (6.12 * pac + 2.37 * msnf) / u.
  const quadraticCoefficient = 0.9 * Math.pow(pac, 2);
  const linearCoefficient = 6.12 * pac + 2.37 * msnfPercentage;
  const temperatureDepression = -temperatureC;

  if (
    temperatureDepression <= 0 ||
    (quadraticCoefficient === 0 && linearCoefficient === 0)
  ) {
    return null;
  }

  const discriminant =
    Math.pow(linearCoefficient, 2) +
    4 * temperatureDepression * quadraticCoefficient;
  const unfrozenWaterProportion =
    (linearCoefficient + Math.sqrt(discriminant)) / (2 * temperatureDepression);
  const frozenWaterPercentage = 1 - unfrozenWaterProportion / waterPercentage;

  if (!Number.isFinite(frozenWaterPercentage)) {
    return null;
  }

  return clamp(frozenWaterPercentage, 0, 1);
}

export function calculateHardeningAdjustment(hardeningFactor: number): number {
  return (
    hardeningFactor *
    HARDENING_FACTOR_FROZEN_WATER_COEFFICIENT *
    PERSONAL_EXPERIENCE_HARDENING_FACTOR_COEFFICIENT
  );
}

export function calculateFirmnessResult(
  rawFrozenWaterPercentage: number | null,
  hardeningFactor: number,
): FirmnessResult | null {
  if (
    rawFrozenWaterPercentage === null ||
    !Number.isFinite(rawFrozenWaterPercentage) ||
    rawFrozenWaterPercentage < 0 ||
    rawFrozenWaterPercentage > 1 ||
    !Number.isFinite(hardeningFactor) ||
    hardeningFactor < 0
  ) {
    return null;
  }

  const hardeningAdjustment = calculateHardeningAdjustment(hardeningFactor);

  return {
    rawFrozenWaterPercentage,
    hardeningAdjustment,
    firmnessEquivalentPercentage: clamp(
      rawFrozenWaterPercentage + hardeningAdjustment,
      0,
      1,
    ),
  };
}

export function calculateEnergyKcalPerScoop(calculations: RecipeCalculations) {
  return calculations.totalWeight > 0
    ? (SCOOP_SIZE_GRAMS * calculations.totalEnergyKcal) /
        calculations.totalWeight
    : 0;
}

export function calculateEnergyKcalPer100g(calculations: RecipeCalculations) {
  return calculations.totalWeight > 0
    ? (100 * calculations.totalEnergyKcal) / calculations.totalWeight
    : 0;
}

export function calculateIngredientMsnfPct(
  ingredient: CalculatorIngredient["ingredient"],
) {
  if (ingredient.category !== "dairy") {
    return 0;
  }

  const waterPct = toNumber(ingredient.waterPct);
  const fatPct = toNumber(ingredient.fatPct);
  const sugarsPct = toNumber(ingredient.sugarsPct);
  const lactosePct = toNumber(ingredient.lactosePct);
  const addedSugarsPct = clamp(sugarsPct - lactosePct, 0, 1);

  return clamp(1 - waterPct - fatPct - addedSugarsPct, 0, 1);
}

function calculateFreezingCurve({
  pac,
  waterPercentage,
  msnfPercentage,
}: {
  pac: number;
  waterPercentage: number;
  msnfPercentage: number;
}) {
  // Include 85%, the upper firmness target for Hard scoop without hardening.
  const frozenWaterPercentages = Array.from({ length: 86 }, (_, i) =>
    Number((i * 0.01).toFixed(2)),
  );
  return frozenWaterPercentages.map((frozenWaterPercentage) => {
    return {
      frozenWaterPercentage,
      freezingPointDepressionCelcius: calculateFreezingPointDepressionSimple({
        pac,
        waterPercentage,
        percentageWaterFrozen: frozenWaterPercentage,
        msnfPercentage,
      }),
    };
  });
}

export function calculateRecipeValues(
  recipeIngredients: CalculatorIngredient[],
  config: CalculationConfiguration,
): RecipeCalculations {
  const mixIngredients = recipeIngredients.filter(
    (ri) => !getInfusionModifier(ri.modifiers),
  );

  if (mixIngredients.length === 0) {
    return {
      totalWeight: 0,
      totalEnergyKcal: 0,
      totalPOD: 0,
      totalPAC: 0,
      fatPercentage: 0,
      saturatedFatPercentage: 0,
      saturatedFatRatio: 0,
      transFatPercentage: 0,
      milkfatPercentage: 0,
      msnfPercentage: 0,
      sugarPercentage: 0,
      sugarNonDairyPercentage: 0,
      stabilizersPercentage: 0,
      stabilizersPerWaterPercentage: 0,
      emulsifiersPercentage: 0,
      totalSolidsPercentage: 0,
      proteinPercentage: 0,
      carbohydratePercentage: 0,
      fiberPercentage: 0,
      totalAlcoholPercentage: 0,
      waterPercentage: 0,
      totalFatGrams: 0,
      totalSugarGrams: 0,
      totalLactoseGrams: 0,
      totalSugarNonDairyGrams: 0,
      totalStabilizersGrams: 0,
      totalWaterGrams: 0,
      totalMSNFGrams: 0,
      totalProteinGrams: 0,
      totalCarbohydrateGrams: 0,
      totalFiberGrams: 0,
      hardeningFactor: 0,
      freezingPointDepressionCelcius: 0,
      freezingCurve: [],
      servingTemperature: 0,
      frozenWaterPercentageAtServingTemperature: null,
      firmnessAtServingTemperature: null,
      scoopabilityFraction: config.desiredFrozenWaterPercentage,
      hardeningFactorAdjustedScoopabilityFraction:
        config.desiredFrozenWaterPercentage,
    };
  }

  let totalWeight = mixIngredients.reduce((sum, ri) => sum + ri.amountGrams, 0);

  let totalEnergyKcal = 0;
  let totalFat = 0;
  let totalSaturatedFat = 0;
  let totalTransFat = 0;
  let totalMilkfat = 0;
  let totalSugar = 0;
  let totalLactose = 0;
  let totalSugarNonDairy = 0;
  let totalWater = 0;
  let totalMSNF = 0;
  let totalStabilizers = 0;
  let totalEmulsifiers = 0;
  let totalProtein = 0;
  let totalAlcohol = 0;
  let totalCarbohydrate = 0;
  let totalFiber = 0;
  let totalPODUnits = 0;
  let totalPACUnits = 0;
  let weightedHardeningFactor = 0;
  let ingredientEvaporationAmount = 0;

  mixIngredients.forEach((ri) => {
    const { ingredient, amountGrams } = ri;
    const proportion = amountGrams / totalWeight;

    totalEnergyKcal += toNumber(ingredient.energyKcal) * amountGrams;
    totalFat += toNumber(ingredient.fatPct) * amountGrams;
    totalSaturatedFat += toNumber(ingredient.saturatedFatPct) * amountGrams;
    totalTransFat += toNumber(ingredient.transFatPct) * amountGrams;
    totalSugar += toNumber(ingredient.sugarsPct) * amountGrams;
    totalLactose += toNumber(ingredient.lactosePct) * amountGrams;
    totalCarbohydrate += toNumber(ingredient.carbPct) * amountGrams;
    totalFiber += toNumber(ingredient.fiberPct) * amountGrams;
    totalWater += toNumber(ingredient.waterPct) * amountGrams;
    totalProtein += toNumber(ingredient.proteinPct) * amountGrams;
    totalAlcohol += toNumber(ingredient.alcoholPct) * amountGrams;
    totalEmulsifiers += toNumber(ingredient.emulsifierPct) * amountGrams;
    totalStabilizers += toNumber(ingredient.stabilizerPct) * amountGrams;
    if (ingredient.category === "dairy") {
      totalMSNF += calculateIngredientMsnfPct(ingredient) * amountGrams;
      totalMilkfat += toNumber(ingredient.fatPct) * amountGrams;
      const lactosePct = toNumber(ingredient.lactosePct);
      totalSugarNonDairy +=
        Math.max(0, toNumber(ingredient.sugarsPct) - lactosePct) * amountGrams;
    } else {
      totalSugarNonDairy += toNumber(ingredient.sugarsPct) * amountGrams;
    }

    // Accumulate POD and PAC units so they can be normalized against the
    // final mix weight after evaporation.
    totalPODUnits += toNumber(ingredient.pod) * amountGrams;
    totalPACUnits += toNumber(ingredient.pac) * amountGrams;

    // Accumulate hardening units (pre-evaporation; water loss does not reduce solids)
    weightedHardeningFactor +=
      calculateHardeningFactor(ingredient) * proportion;

    const requestedIngredientEvaporation =
      amountGrams *
      clamp(getEvaporationModifier(ri.modifiers)?.percentage ?? 0, 0, 1);
    const ingredientWater = toNumber(ingredient.waterPct) * amountGrams;
    ingredientEvaporationAmount += Math.min(
      requestedIngredientEvaporation,
      ingredientWater,
    );
  });

  totalWater = Math.max(0, totalWater - ingredientEvaporationAmount);
  totalWeight = Math.max(0, totalWeight - ingredientEvaporationAmount);

  // Apply evaporation if specified
  if (config.evaporationPercentage > 0) {
    const evaporationAmount = totalWeight * config.evaporationPercentage;

    // Remove water first, up to the evaporation amount
    const waterToRemove = Math.min(evaporationAmount, totalWater);
    totalWater -= waterToRemove;

    // If there's still evaporation left after removing all water,
    // we have an invalid scenario but we'll just cap it at total water
    const actualEvaporationAmount = waterToRemove;
    totalWeight -= actualEvaporationAmount;

    // Ensure we don't have negative values
    totalWater = Math.max(0, totalWater);
    totalWeight = Math.max(0, totalWeight);
  }

  if (totalWeight <= 0) {
    return calculateRecipeValues([], config);
  }

  const fatPercentage = totalFat / totalWeight;
  const saturatedFatPercentage = totalSaturatedFat / totalWeight;
  const saturatedFatRatio =
    totalFat > 0
      ? clamp((totalSaturatedFat + totalTransFat) / totalFat, 0, 1)
      : 0;
  const transFatPercentage = totalTransFat / totalWeight;
  const milkfatPercentage = totalMilkfat / totalWeight;
  const sugarPercentage = totalSugar / totalWeight;
  const sugarNonDairyPercentage = totalSugarNonDairy / totalWeight;
  const waterPercentage = totalWater / totalWeight;
  const msnfPercentage = totalMSNF / totalWeight;
  const stabilizersPercentage = totalStabilizers / totalWeight;
  const stabilizersPerWaterPercentage =
    totalWater > 0 ? totalStabilizers / totalWater : 0;
  const emulsifiersPercentage = totalEmulsifiers / totalWeight;
  const totalAlcoholPercentage = totalAlcohol / totalWeight;
  const totalSolidsPercentage = 1 - waterPercentage - totalAlcoholPercentage;
  const totalProteinPercentage = totalProtein / totalWeight;
  const totalCarbohydratePercentage = totalCarbohydrate / totalWeight;
  const totalFiberPercentage = totalFiber / totalWeight;
  const weightedPOD = totalPODUnits / totalWeight;
  const weightedPAC = totalPACUnits / totalWeight;

  const freezingPointDepressionCelcius = calculateFreezingPointDepressionSimple(
    {
      pac: weightedPAC,
      waterPercentage,
      percentageWaterFrozen: 0,
      msnfPercentage,
    },
  );
  const freezingCurve = calculateFreezingCurve({
    pac: weightedPAC,
    waterPercentage,
    msnfPercentage,
  });
  const hardeningFactorAdjustedScoopabilityFraction =
    config.desiredFrozenWaterPercentage -
    calculateHardeningAdjustment(weightedHardeningFactor);
  const servingTemperature = calculateFreezingPointDepressionSimple({
    pac: weightedPAC,
    waterPercentage,
    percentageWaterFrozen: hardeningFactorAdjustedScoopabilityFraction,
    msnfPercentage,
  });
  const frozenWaterPercentageAtServingTemperature =
    config.servingTemperatureC === undefined
      ? null
      : calculateFrozenWaterPercentageAtTemperature({
          pac: weightedPAC,
          waterPercentage,
          msnfPercentage,
          temperatureC: config.servingTemperatureC,
        });
  const firmnessAtServingTemperature = calculateFirmnessResult(
    frozenWaterPercentageAtServingTemperature,
    weightedHardeningFactor,
  );

  return {
    totalEnergyKcal,
    totalPOD: weightedPOD,
    totalPAC: weightedPAC,
    fatPercentage,
    saturatedFatPercentage,
    saturatedFatRatio,
    transFatPercentage,
    milkfatPercentage,
    sugarPercentage,
    sugarNonDairyPercentage,
    msnfPercentage: msnfPercentage,
    stabilizersPercentage: stabilizersPercentage,
    stabilizersPerWaterPercentage,
    emulsifiersPercentage: emulsifiersPercentage,
    totalSolidsPercentage: totalSolidsPercentage,
    proteinPercentage: totalProteinPercentage,
    carbohydratePercentage: totalCarbohydratePercentage,
    waterPercentage,
    fiberPercentage: totalFiberPercentage,
    totalAlcoholPercentage: totalAlcoholPercentage,
    totalWeight,
    totalFatGrams: totalFat,
    totalSugarGrams: totalSugar,
    totalLactoseGrams: totalLactose,
    totalSugarNonDairyGrams: totalSugarNonDairy,
    totalStabilizersGrams: totalStabilizers,
    totalWaterGrams: totalWater,
    totalMSNFGrams: totalMSNF,
    totalProteinGrams: totalProtein,
    totalCarbohydrateGrams: totalCarbohydrate,
    totalFiberGrams: totalFiber,
    servingTemperature,
    frozenWaterPercentageAtServingTemperature,
    firmnessAtServingTemperature,
    hardeningFactor: weightedHardeningFactor,
    freezingPointDepressionCelcius,
    freezingCurve,
    scoopabilityFraction: config.desiredFrozenWaterPercentage,
    hardeningFactorAdjustedScoopabilityFraction,
  };
}
